import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { graphql, Link, useStaticQuery } from 'gatsby';
import { ProductTile } from '@pella/sash-framework';
import { LoadingOverlay } from '../../../../components';
import { useOktaAuth } from '../../../../hooks/use-okta-auth';
import { captureMessage, getCurrentScope, setContext, setTag } from '@sentry/gatsby';
import { useProduct } from '../../../../hooks/use-product';
import { isValidArray } from '../../../../components/utilities/validations';

const ResultsTile = ({ tile, type, pdpURL, dynamicPageState, hideCompare, className, btnText, btnColor, variant, loading: loadingOverride, showPrice = true, onHover, wrapperClass }) => {
	const data = useStaticQuery(query);
	const content = data.contentstackPlpConfig;
	const staticTile = data.allProductTiles.nodes.find((productTile) => productTile.backend_name === tile.backend_name);
	const locations = useSelector((state) => state.locations);
	const reduxTiles = useSelector((state) => state.ecommData?.tiles);
	const { profile } = useOktaAuth();
	const [tileData, setTileData] = useState(dynamicPageState ? { ...tile } : { ...tile, ...staticTile });
	const [isPro, setIsPro] = useState(false);
	const [isEcommerce, setIsEcommerce] = useState(false);
	const [isPartsOnly, setIsPartsOnly] = useState(false);
	const [showPriceAmount, setShowPriceAmount] = useState(true);
	const [bundleData, setBundleData] = useState({});
	const [loading, setLoading] = useState(loadingOverride);
	const { getBundle } = useProduct();

	useEffect(() => {
		if (dynamicPageState) setTileData(tile);
	}, [dynamicPageState, tile]);

	useEffect(() => {
		const fetchBundleData = async (id, client_id) => {
			const bundle = await getBundle(id, client_id);

			if (bundle?.data?.bundle_items?.[0]) {
				setBundleData(bundle.data);
			} else {
				if (bundle?.data?.error) {
					setTag('Pella Component', 'AddToCart', 'API Transaction ID', bundle?.data?.REQUEST_ID);
					getCurrentScope().setTransactionName('getBundle');
					setContext('Error', {
						message: bundle?.data?.error?.message,
						details: bundle?.data?.error?.details,
					});
					captureMessage(bundle.data?.error || 'Unknown Error');
				}

				return null;
			}
		};

		if (profile.isPro && !isPro) {
			setIsPro(true);
		} else if (!profile.isPro && isPro) {
			setIsPro(false);
		}

		if (tile?.bundle) {
			fetchBundleData(tile.backend_name, profile.isPro ? 'H04' : 'H03');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- function as dep causes infinite loop (fix later)
	}, [profile.isPro, isPro]);

	useEffect(() => {
		if (typeof locations.ecommerce === 'boolean' && isEcommerce !== locations.ecommerce) {
			setIsEcommerce(locations.ecommerce);
		}
		if (typeof locations.parts_only === 'boolean' && isPartsOnly !== locations.parts_only) {
			setIsPartsOnly(locations.parts_only);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- function as dep causes infinite loop (fix later)
	}, [locations.ecommerce, locations.parts_only, isEcommerce, isPartsOnly]);

	useEffect(() => {
		if (!isEcommerce || (isPartsOnly && type !== 'part')) {
			setShowPriceAmount(false);
			setLoading(false);
		} else {
			setShowPriceAmount(true);
			setLoading(false);
		}
	}, [isEcommerce, isPartsOnly, type]);

	useEffect(() => {
		if (isValidArray(reduxTiles) && !tile?.manual) {
			const reduxTile = reduxTiles.find((item) => item.backend_name === tile.backend_name);

			if (reduxTile && (reduxTile?.price?.consumer?.amount !== tileData?.price?.consumer?.amount || reduxTile?.price?.professional?.amount !== tileData?.price?.professional?.amount)) {
				setTileData((current) => ({ ...current, ...reduxTile }));
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- only watch redux changes
	}, [reduxTiles]);

	function handleTileClick() {
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({
			event: 'productClick',
			ecommerce: {
				click: {
					products: [
						{
							id: tile.mpn || tile.backend_name,
							name: tile.backend_name,
							category: tile.display_name,
							priceRange: type !== 'part' ? tile.description : null,
						},
					],
				},
			},
		});
		localStorage.setItem('plpurl', window.location); //store url for continue shopping button in cart
	}

	const isHighlighted = content?.highlighted_items?.find((item) => item === tileData?.backend_name) ? true : false;
	const isQuickShip = content?.quick_ship?.find((item) => item === tileData?.backend_name) ? true : false;
	const productName = !tile?.bundle && tileData?.material ? tileData?.material + ' ' + tileData?.display_name : !tile?.bundle && !tileData?.material ? tileData?.display_name : bundleData?.display_name;
	const productPrice = showPriceAmount && tileData?.available_online && tileData?.price?.consumer?.amount > 0 ? { pro: isPro ? tileData?.price?.professional?.formatted : null, consumer: tileData?.price?.consumer?.formatted } : null;

	const onTileEnter = () => {
		if (typeof onHover === 'function') onHover(tileData.backend_name);
		return;
	};

	const onTileLeave = () => {
		if (typeof onHover === 'function') onHover(null);
		return;
	};

	return (
		<div
			className={`relative h-full ${wrapperClass || ''}`}
			onMouseEnter={onTileEnter}
			onMouseLeave={onTileLeave}
		>
			{loading && (
				<LoadingOverlay
					fixed={false}
					svgSize={200}
					className='!bg-white/70'
				/>
			)}

			<ProductTile
				image={tileData?.manual_image?.url ? tileData.manual_image.url + '?format=pjpg&auto=webp&quality=90' : tileData?.image_url}
				hoverImage={tileData?.hover_image?.url ? tileData.hover_image.url + '?format=pjpg&auto=webp&quality=90' : null}
				productLine={!tile?.bundle ? tileData?.header : bundleData?.header}
				productName={productName}
				price={productPrice}
				pricingText={isPro ? content?.pro_price : content?.price_text}
				noPriceText={content?.pricing_na}
				link={pdpURL}
				linkComponent={Link}
				onClick={handleTileClick}
				className={`contentKing ${className || ''}`}
				layout={variant}
				buttonText={btnText}
				buttonColor={btnColor}
				startingAt={content?.starting_at}
				showPrice={showPrice}
				isGatsby
			>
				{isHighlighted && !isQuickShip && (
					<div className='absolute left-2 top-2 z-2 grid h-12 w-12 place-items-center rounded-full bg-primary text-center md:left-4 md:top-4'>
						<p className='text-xs font-semibold uppercase leading-3 text-black'>{content?.tile_labels?.[0]}</p>
					</div>
				)}

				{isQuickShip && (
					<div className='absolute left-2 top-2 z-2 grid h-12 w-12 place-items-center rounded-full bg-black text-center md:left-4 md:top-4'>
						<p className='text-xs font-semibold uppercase leading-3 text-primary'>{content?.tile_labels?.[1]}</p>
					</div>
				)}
			</ProductTile>
		</div>
	);
};

ResultsTile.propTypes = {
	tile: PropTypes.object,
	pdpURL: PropTypes.string,
	dynamicPageState: PropTypes.bool,
	hideCompare: PropTypes.bool,
	type: PropTypes.string,
	className: PropTypes.string,
	btnText: PropTypes.string,
	btnColor: PropTypes.oneOf(['primary', 'primary-alt', 'secondary', 'tertiary']),
	variant: PropTypes.oneOf(['vertical', 'horizontal']),
	loading: PropTypes.bool,
	showPrice: PropTypes.bool,
	onHover: PropTypes.func,
	wrapperClass: PropTypes.string,
};

export const query = graphql`
	{
		allProductTiles {
			nodes {
				...ProductTile
			}
		}

		contentstackPlpConfig {
			tile_labels
			price_text
			pro_price
			pricing_na
			highlighted_items
			quick_ship
			starting_at
		}
	}
`;

export default ResultsTile;
